import { Box, Container, Flex, Text } from "@theme-ui/components"
import React, { useState } from "react"
import { useSecondaryMenu } from "../hooks/useSecondaryMenu"
import { getHomePath, getSearchPath } from "../utils/path"
import { InboundLink, OutboundLink } from "./link"
import { MagicLink } from "../utils/magicLink"
import LanguageSwitcher from "./languageSwitcher"
import Logo from "../assets/images/logo.svg"
import { LanguageSwitcherContext } from "../context/languageSwitcherContext"
import { ChevronDown, Phone } from "react-feather"
import { getColor } from "@theme-ui/color"
import themeUiTheme from "../gatsby-plugin-theme-ui"
import { motion, AnimatePresence } from "framer-motion"
import { debounce } from "lodash"
import GreenNumber from "./greenNumber"
import ShopCart from "../assets/images/shop-cart.svg"

const Nav = ({ color = "light" }) => {
  const locale = React.useContext(LanguageSwitcherContext).activeLocale
  const useMenuData = useSecondaryMenu()
  const menu = useMenuData.allDatoCmsSecondaryMenu.nodes.filter(
    link => link.locale === locale
  )

  const productFamily = useMenuData.allDatoCmsProductFamily
  const productCategory = useMenuData.allDatoCmsProductCategory

  menu.map(menuItem => {
    menuItem.treeChildren.sort((a, b) => a.position - b.position)
    menuItem.treeChildren.map(menuItem => {
      if (menuItem.treeChildren.length > 0) {
        menuItem.treeChildren.sort((a, b) => a.position - b.position)
      }
    })
  })

  return (
    <Box
      as="nav"
      variant="nav"
      sx={{ zIndex: 5, position: "relative", display: "block" }}
    >
      <Container variant="header">
        <Flex sx={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Box sx={{ paddingX: 0, paddingY: 3 }}>
            <InboundLink
              to={getHomePath(locale)}
              sx={{ svg: { height: ["80px"] } }}
            >
              <Logo />
            </InboundLink>
          </Box>
          <Flex
            sx={{
              flexDirection: "row",
              padding: 0,
              margin: 0,
              listStyle: "none",
              alignItems: "center",
            }}
            as="ul"
          >
            {menu.map((item, index) => (
              <>
                <TextComponent
                  item={item}
                  color={color}
                  locale={locale}
                  key={item.id}
                  index={index}
                  productFamily={productFamily}
                  productCategory={productCategory}
                />
              </>
            ))}
            <Box
              as="li"
              sx={{
                position: "relative",
                marginX: 1,
                paddingX: 2,
                paddingY: 3,
                pb: [4],
                ml: [6],
                a: {
                  textDecoration: "none",
                },
                "a:hover": {
                  color: "primary",
                },
              }}
            >
              <GreenNumber />
            </Box>
          </Flex>
        </Flex>
      </Container>
    </Box>
  )
}

const TextComponent = ({
  item,
  locale,
  productFamily,
  color,
  index,
  productCategory,
}) => {
  const themeColor = getColor(themeUiTheme, color)

  const debouncedHandleMouseEnterMenu = debounce(() => {
    setShow(true)
  }, 200)

  const handlOnMouseLeaveMenu = () => {
    setShow(false)
    debouncedHandleMouseEnterMenu.cancel()
  }

  const [show, setShow] = useState(false)
  return (
    <Box
      as="li"
      key={item.id}
      sx={{
        position: "relative",
        marginX: 1,
        paddingX: 2,
        pb: item.anchor.toLowerCase() !== "shop" && [3],
        mb: item.anchor.toLowerCase() == "shop" && [3],
        pl: [3],
        paddingTop: 0,
        a: {
          textDecoration: "none",
        },
        "a:hover": {
          color: "primary",
          "svg path": {
            stroke: "primary",
          },
        },
      }}
      onMouseEnter={() => debouncedHandleMouseEnterMenu()}
      onMouseLeave={() => handlOnMouseLeaveMenu()}
    >
      {item.link ? (
        <MagicLink
          sx={{ color, display: "block" }}
          item={item.link}
          locale={locale}
          productFamily={productFamily}
          productCategory={productCategory}
        >
          {item.anchor.toLowerCase() === "shop" ? (
            <Flex
              sx={{
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  mr: [2],
                  svg: {
                    width: "20px",
                    height: "auto",
                    stroke: "light",
                  },
                }}
              >
                <ShopCart />
              </Box>
              <Box>{item.anchor}</Box>
            </Flex>
          ) : (
            item.anchor
          )}
        </MagicLink>
      ) : (
        <Flex sx={{ alignItems: "center" }}>
          <Box sx={{ cursor: "default", color }}>{item.anchor}</Box>
          {item.treeChildren.length > 0 && (
            <ChevronDown color={themeColor} size={16} />
          )}
        </Flex>
      )}
      {item.treeChildren.length > 0 && (
        <motion.div
          initial={{
            y: 0,
            opacity: 0,
            visibility: "hidden",
            transition: {
              visibility: {
                delay: 0.3,
              },
            },
          }}
          animate={{
            visibility: show ? "visible" : "hidden",
            transition: {
              visibility: {
                delay: !show ? 0.3 : 0,
              },
            },
            y: show ? -24 : 0,
            opacity: show ? 1 : 0,
            zIndex: show ? 0 : -20,
          }}
          transition={{ type: "tween", stiffness: 50 }}
          style={{
            zIndex: -20,
            position: "absolute",
            left: 0,
            width: "max-content",
          }}
        >
          <Box
            as="ul"
            sx={{
              listStyle: "none",
              padding: 3,
              margin: 0,
              position: "absolute",
              top: 40,
              width: "max-content",
              boxShadow:
                "0 4px 6px rgb(50 50 93 / 11%), 0 1px 3px rgb(0 0 0 / 8%)",
              borderRadius: "sm",
              backgroundColor: "light",
            }}
          >
            {item.treeChildren.map(item =>
              item.anchor ? (
                <Box as="li" sx={{ py: [2] }} key={item.id}>
                  {item.link ? (
                    <MagicLink
                      sx={{ color: "text" }}
                      item={item.link}
                      locale={locale}
                      productFamily={productFamily}
                      productCategory={productCategory}
                    />
                  ) : (
                    <Box sx={{ cursor: "default" }}>{item.anchor}</Box>
                  )}
                </Box>
              ) : null
            )}
          </Box>
        </motion.div>
      )}
    </Box>
  )
}

export default Nav
